import React, { useMemo } from 'react';
import { ContentPage } from '../MerchandiserLayout';
import { useNavigate } from 'react-router-dom';
import { useShowErrorMessage, useShowMessage } from '../../../../../util/hooks';
import { getPolyglot } from '../../../../../i18n';
import { useAdaptableParams } from '../../../../../util/hooks/useAdaptableParams';
import WidthRefResize from '../../../../../util/renderProps/WidthRefResize';
import { Alert, Backdrop, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ComboCharts } from '../../../../../components/DeviceManager/Device/Monitoring/components/ComboCharts/ComboCharts';
import moment from 'moment';
import { TIME_FRAME_TYPES, TIME_RANGE_TYPES } from 'stoerk-ui-components';
import { useDevice } from '../../../../../redux/commander/hooks/useDevice';
import { ChartsFiltersComposable } from '../../../../../components/DeviceManager/Device/Monitoring/components/ChartsFilters';
import { useMetricsComposable } from '../../../../../redux/monitoring/hooks/useMetricsComposable';

function MerchandiserMonitoring() {
  const params = useAdaptableParams<{
    groupId: string;
    merchandiserId: string;
    deviceId: string;
  }>();
  const polyglot = getPolyglot();
  const showMessage = useShowMessage();
  const showErrorMessage = useShowErrorMessage();
  const location = {
    state: '',
    key: '',
    pathname: '',
    search: '',
    hash: '',
  };
  const navigate = useNavigate();
  const { deviceId, groupId } = params;

  /** Init Groups */
  useDevice(deviceId);
  /** Init Monitoring */
  const initialMemoizedTimeFrame = useMemo(() => {
    return {
      start: moment().subtract(6, 'hours'),
      end: moment(),
      timeFrameType: TIME_FRAME_TYPES.LAST_6_HOURS,
      timeRangeType: TIME_RANGE_TYPES.QUICK,
    };
  }, []);

  /** Init Monitoring */
  const {
    timeControls: { timeFrame, setTimeFrame },
    timeControls,
    analogControls,
    digitalControls,
    labStateFilters,
    loading,
    metrics,
    error: fetchError,
  } = useMetricsComposable({
    defaultTimeFrame: initialMemoizedTimeFrame,
    composableDeviceId: groupId || '',
  });

  return (
    <ContentPage
      variant="elevation"
      sx={{
        flex: '1',
        width: '100%',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      <WidthRefResize
        render={(width) => (
          <div id="monitoring-charts">
            {
              <div>
                <ChartsFiltersComposable
                  polyglot={polyglot}
                  onChangeDatePicker={setTimeFrame}
                  groupId={groupId ?? ''}
                  analogControls={analogControls}
                  digitalControls={digitalControls}
                  timeFrame={timeFrame}
                  labStateFilters={labStateFilters}
                />
                {loading ? (
                  <Backdrop
                    sx={{
                      backgroundColor: '#ffffffA2',
                      zIndex: 1001,
                    }}
                    open={loading}
                  >
                    <Box
                      display={'flex'}
                      flexDirection={'column'}
                      alignItems={'center'}
                    >
                      <CircularProgress color="inherit" />
                      <Typography>{polyglot.t('general.loading')}</Typography>
                    </Box>
                  </Backdrop>
                ) : fetchError ? (
                  <Alert severity="warning">
                    {polyglot.t('error.unknown')}
                  </Alert>
                ) : (
                  <ComboCharts
                    digitalChannels={metrics.digitalChannels}
                    sensordata={metrics.sensordata.map((sensordata) => ({
                      ...sensordata,
                      data: sensordata.data.map((data) => ({
                        ...data,
                        val: Number(data.val),
                      })),
                    }))}
                    timeControls={timeControls}
                    timeFrame={timeFrame}
                    maxWidth={width - 32}
                    handlingErrorsApi={showErrorMessage}
                    location={location}
                    navigate={navigate}
                    params={params}
                    polyglot={polyglot}
                    showMessage={showMessage}
                    sectionsNavigation={[]}
                  />
                )}
              </div>
            }
          </div>
        )}
      />
    </ContentPage>
  );
}

export default MerchandiserMonitoring;
